/**
 * Wrapper fino do fetch. Sempre `credentials: include` pro cookie de sessão.
 * Lança erro com status pra UI tratar.
 */

export class ApiError extends Error {
  constructor(public status: number, public body: unknown) {
    super(`API ${status}`);
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = (await res.json().catch(() => null)) as unknown;

  if (!res.ok) {
    throw new ApiError(res.status, data);
  }

  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

// — Endpoints tipados —

export interface AuthMe {
  user: { username: string };
}

export interface JobRunSummary {
  startedAt: string;
  finishedAt: string | null;
  status: 'running' | 'success' | 'failed';
  durationMs: number | null;
  errorMessage: string | null;
  triggerSource?: 'cron' | 'manual';
}

export interface JobInfo {
  name: string;
  displayName: string | null;
  description: string;
  scheduleDefault: string;
  scheduleIsOverridden: boolean;
  schedule: string;
  timezone: string;
  lastRun: JobRunSummary | null;
}

export interface JobsList {
  jobs: JobInfo[];
}

export const authApi = {
  me: () => api.get<AuthMe>('/api/me'),
  login: (username: string, password: string) =>
    api.post<{ ok: boolean; user: { username: string } }>('/login', { username, password }),
  logout: () => api.post<{ ok: boolean }>('/logout'),
};

export interface JobLogEntry {
  timestamp: string;
  level: 'debug' | 'info' | 'warn' | 'error';
  message: string;
  data: Record<string, unknown> | null;
}

export interface CostTokens {
  prompt: number;
  cached: number;
  completion: number;
  /** Parte da saída que o cliente nunca lê: o modelo pensando. */
  reasoning: number;
}

/** Gasto em dólar separado nas três faixas cobradas. Ver CostSplitUsd no backend. */
export interface CostSplitUsd {
  inputCacheMiss: number;
  inputCacheHit: number;
  output: number;
}

export interface ClientCost {
  tenantId: string;
  name: string | null;
  runs: number;
  calls: number;
  tokens: CostTokens;
  usd: number;
  brl: number;
  split: CostSplitUsd;
  unpricedCalls: number;
}

/** De onde veio o gasto: atendimento de cliente, ou uma rotina interna nossa
 *  (relatório diário, priorização de carteira). */
export type CostOrigin = 'workflow' | 'daily_report';

export interface WorkflowCost {
  origin: CostOrigin;
  workflowDefinitionId: string | null;
  slug: string | null;
  name: string | null;
  tenantId: string;
  runs: number;
  calls: number;
  tokens: CostTokens;
  usd: number;
  brl: number;
  split: CostSplitUsd;
  unpricedCalls: number;
}

export interface ModelCost {
  provider: string | null;
  model: string | null;
  priced: boolean;
  runs: number;
  calls: number;
  tokens: CostTokens;
  usd: number;
  brl: number;
  split: CostSplitUsd;
}

export type CostScope = 'cliente' | 'workflow';

/** O corpo comum de qualquer recorte do detalhe. */
export interface CostSlice {
  calls: number;
  runs: number;
  tokens: CostTokens;
  usd: number;
  brl: number;
  split: CostSplitUsd;
}

export interface CostDetail {
  period: { from: string; to: string };
  identity: {
    scope: 'client' | 'workflow';
    id: string;
    name: string;
    subtitle: string | null;
    tenantId: string;
    tenantName: string | null;
    origin: CostOrigin;
  };
  total: CostSlice;
  byDay: (CostSlice & { day: string })[];
  byAction: (CostSlice & { action: string })[];
  byStep: (CostSlice & { step: string })[];
  byModel: (CostSlice & { provider: string | null; model: string | null; priced: boolean })[];
  byWorkflow: (CostSlice & { id: string; name: string; origin: CostOrigin })[];
  costliestRuns: {
    executionId: string;
    startedAt: string;
    calls: number;
    tokens: CostTokens;
    usd: number;
    brl: number;
  }[];
}

export interface CostBreakdown {
  period: { from: string; to: string };
  total: {
    runs: number;
    calls: number;
    tokens: CostTokens;
    usd: number;
    brl: number;
    split: CostSplitUsd;
    unpricedCalls: number;
  };
  byClient: ClientCost[];
  byWorkflow: WorkflowCost[];
  byModel: ModelCost[];
}

export const costsApi = {
  breakdown: (from?: string, to?: string) =>
    api.get<CostBreakdown>(`/api/costs${periodQuery(from, to)}`),
  detail: (scope: CostScope, id: string, from?: string, to?: string) =>
    api.get<CostDetail>(`/api/costs/${scope}/${encodeURIComponent(id)}${periodQuery(from, to)}`),
};

function periodQuery(from?: string, to?: string): string {
  const q = new URLSearchParams();
  if (from) q.set('from', from);
  if (to) q.set('to', to);
  const qs = q.toString();
  return qs ? `?${qs}` : '';
}

export const jobsApi = {
  list: () => api.get<JobsList>('/api/jobs'),
  trigger: (name: string) => api.post<{ ok: boolean }>(`/api/jobs/${name}/trigger`),
  runs: (name: string) => api.get<{ runs: JobRunSummary[] }>(`/api/jobs/${name}/runs`),
  logs: (name: string, limit = 50) =>
    api.get<{ logs: JobLogEntry[] }>(`/api/jobs/${name}/logs?limit=${limit}`),
  updateSchedule: (name: string, schedule: string) =>
    api.put<{ ok: boolean; schedule: string }>(`/api/jobs/${name}/schedule`, {
      schedule,
    }),
};

/** Um cliente inscrito no relatório. */
export interface ReportClient {
  tenantId: string;
  name: string;
}

/** Contato do Desenrola HQ: o único que entra na lista de envio do WhatsApp. */
export interface HeadquartersContact {
  id: string;
  name: string;
  identification: string | null;
}

export type ReportCadence = 'daily' | 'weekly';

export interface ReportWhatsappSubscription {
  recipientId: number;
  cadence: ReportCadence;
}

/** Um contato na lista do WhatsApp com o que ele recebe: o diário, o semanal ou os dois. */
export interface ReportWhatsappContactRow {
  contact: HeadquartersContact;
  subscriptions: ReportWhatsappSubscription[];
}

export interface ClientReportRecipients {
  whatsapp: ReportWhatsappContactRow[];
  email: { name: string; email: string }[];
}

export const reportRecipientsApi = {
  clients: () => api.get<ReportClient[]>('/api/report-recipients/clients'),
  contacts: (search: string) =>
    api.get<HeadquartersContact[]>(
      `/api/report-recipients/contacts?search=${encodeURIComponent(search)}`,
    ),
  of: (tenantId: string) =>
    api.get<ClientReportRecipients>(`/api/report-recipients/clients/${encodeURIComponent(tenantId)}`),
  add: (tenantId: string, hqCustomerId: string, cadence: ReportCadence) =>
    api.post<{ id: number }>(
      `/api/report-recipients/clients/${encodeURIComponent(tenantId)}/whatsapp`,
      { hqCustomerId, cadence },
    ),
  remove: (tenantId: string, recipientId: number) =>
    api.delete<null>(
      `/api/report-recipients/clients/${encodeURIComponent(tenantId)}/whatsapp/${recipientId}`,
    ),
};

export type ReportScheduleUnit = 'hour' | 'day' | 'week' | 'month';

export interface ReportDefinitionSchedule {
  interval: number;
  unit: ReportScheduleUnit;
  days: number[];
  startsOn: string;
  sendTime: string;
}

export type ReportDefinitionChannel = 'whatsapp' | 'email';

/** Um conjunto de relatório: o modelo e os canais valem para todas as linhas de cliente dele. */
export interface ReportDefinitionGroup {
  id: number;
  name: string;
  templateName: string;
  channels: ReportDefinitionChannel[];
}

/** O contato recebe por todos os canais do conjunto em que tem cadastro. */
export interface ReportDefinitionRecipientInput {
  hqCustomerId: string;
}

/** Quem recebe numa linha de cliente: o contato do HQ, nulo quando saiu de lá. */
export interface ReportLineContact {
  hqCustomerId: string;
  contact: HeadquartersContact | null;
}

/** Uma linha de cliente num conjunto: de quem é, para quem vai e como foi o último envio. */
export interface ReportDefinitionSummary {
  id: number;
  tenantId: string;
  tenantName: string | null;
  reportDefinitionGroupId: number;
  status: 'active' | 'paused';
  schedule: ReportDefinitionSchedule;
  recipients: (ReportLineContact & { id: number })[];
  lastSentAt: string | null;
  openFailure: { reportDate: string; failure: string; detail: string } | null;
}

/** Uma linha do histórico da definição: o quê mudou, de quanto para quanto, quem e quando. */
export interface ReportDefinitionChange {
  id: number;
  field: string | null;
  action: string;
  before: unknown;
  after: unknown;
  changedBy: string | null;
  source: string;
  createdAt: string;
}

/**
 * Como o relatório chegou a um contato num canal, na hora do último status. O e-mail vai até `opened`, pelo pixel do SES,
 * que o Apple Mail e o Gmail baixam sozinhos. A origem da falha (`send`: a API recusou; `meta`: a Meta não entregou) só o
 * WhatsApp conta.
 */
export interface ReportDefinitionSendOutcome {
  status: 'read' | 'opened' | 'delivered' | 'delayed' | 'sent' | 'failed';
  at: string;
  error: string | null;
  failureOrigin: 'send' | 'meta' | null;
}

/** Um contato num envio; sem `hqCustomerId` é o e-mail que nenhum contato tem mais, mostrado pelo endereço. */
export interface ReportDefinitionSendDelivery {
  hqCustomerId: string | null;
  contactName: string | null;
  recipientEmail: string | null;
  whatsapp: ReportDefinitionSendOutcome | null;
  email: ReportDefinitionSendOutcome | null;
}

/** Um motivo de falha de um envio e em quantos contatos e canais aconteceu; sem `error` quando veio sem motivo. */
export interface ReportDefinitionSendFailureReason {
  error: string | null;
  count: number;
}

/** A conta de um envio por contato e canal, com as falhas agrupadas pelo motivo, da mais comum à mais rara. */
export interface ReportDefinitionSendTally {
  reached: number;
  attempted: number;
  failureReasons: ReportDefinitionSendFailureReason[];
}

/** Um relatório que a linha mandou, com o período que cobre e a conta de como chegou. Os contatos vêm por `deliveries`. */
export interface ReportDefinitionSend {
  reportId: string;
  reportDate: string;
  /** Nulo quando a quinzena foi gerada e nenhum canal saiu: o envio aparece pelo dia do relatório. */
  sentAt: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  tally: ReportDefinitionSendTally;
}

/** Uma página de uma lista da tela de envios. */
/** Uma página dos contatos de um envio, só as falhas ou todos. */
export interface ReportSendDeliveriesQuery {
  definition: ReportDefinitionSummary;
  reportId: string;
  onlyFailed: boolean;
  page: number;
}

export interface ReportDefinitionPage<T> {
  items: T[];
  total: number;
  page: number;
  perPage: number;
}

export interface ReportDefinitionTenant {
  tenantId: string;
  name: string;
}

export interface ReportDefinitionCreation {
  reportDefinitionGroupId: number;
  schedule: ReportDefinitionSchedule;
  recipients: ReportDefinitionRecipientInput[];
}

export interface ReportDefinitionUpdate {
  schedule?: ReportDefinitionSchedule;
  recipients?: ReportDefinitionRecipientInput[];
}

export const reportDefinitionsApi = {
  list: () => api.get<ReportDefinitionSummary[]>('/api/report-definitions'),
  groups: () => api.get<ReportDefinitionGroup[]>('/api/report-definition-groups'),
  tenants: () => api.get<ReportDefinitionTenant[]>('/api/report-definitions/tenants'),
  create: (tenantId: string, body: ReportDefinitionCreation) =>
    api.post<{ id: number }>(`/api/report-definitions/${encodeURIComponent(tenantId)}`, body),
  update: (definition: ReportDefinitionSummary, body: ReportDefinitionUpdate) =>
    api.patch<unknown>(definitionPath(definition), body),
  pause: (definition: ReportDefinitionSummary) => api.post<unknown>(`${definitionPath(definition)}/pause`),
  resume: (definition: ReportDefinitionSummary) => api.post<unknown>(`${definitionPath(definition)}/resume`),
  remove: (definition: ReportDefinitionSummary) => api.delete<null>(definitionPath(definition)),
  changes: (definition: ReportDefinitionSummary) =>
    api.get<ReportDefinitionChange[]>(`${definitionPath(definition)}/changes`),
  sends: (definition: ReportDefinitionSummary, page: number) =>
    api.get<ReportDefinitionPage<ReportDefinitionSend>>(`${definitionPath(definition)}/sends?page=${page}`),
  deliveries: ({ definition, reportId, onlyFailed, page }: ReportSendDeliveriesQuery) =>
    api.get<ReportDefinitionPage<ReportDefinitionSendDelivery>>(
      `${definitionPath(definition)}/sends/${reportId}/deliveries?page=${page}&onlyFailed=${onlyFailed}`,
    ),
};

function definitionPath(definition: ReportDefinitionSummary): string {
  return `/api/report-definitions/${encodeURIComponent(definition.tenantId)}/${definition.id}`;
}
